import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';
import { MachineService } from '../api/api_pb';
import { S3Service } from '../api/api_pb';
import { isValidEmail } from '../utils/validation';
import { useMachineByUuid } from '../hooks/useMachineByUuid';
import { CreditCard } from 'lucide-react';

const EditMachine: React.FC = () => {
    const { machineUuid } = useParams();
    const machineClient = useClient(MachineService);
    const s3Client = useClient(S3Service);
    const navigate = useNavigate();
    const { machine, isLoading } = useMachineByUuid(machineUuid);

    const [machineName, setMachineName] = useState('');
    const [newPinCode, setNewPinCode] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [contactNumber, setContactNumber] = useState('');
    const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');

    useEffect(() => {
        if (machine) {
            setMachineName(machine.name);
            setNewPinCode(machine.pinCode);
            setImageUrl(machine.splashScreenUrl);
            setContactNumber(machine.contactNumber);
            setNotificationEmails(machine.notificationEmails || []);
        }
    }, [machine]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.size <= 2 * 1024 * 1024) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            toast.error('Please select an image under 2MB');
        }
    };

    const getPresignedUrl = async (): Promise<string> => {
        if (!imageFile) return '';
        const extension = imageFile.name.split('.').pop() || '';
        const response = await s3Client.getUploadUrl({ extension });
        return response.url;
    };

    const uploadImage = async (url: string) => {
        if (!imageFile) return;

        const res = await fetch(url, {
            method: 'PUT',
            body: imageFile,
            headers: {
                'Content-Type': imageFile.type,
            },
        });

        if (!res.ok) {
            toast.dismiss();
            toast.error('Image upload failed');
            throw new Error('Image upload failed');
        }
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail();
        }
    };

    const addEmail = () => {
        const trimmedEmail = emailInput.trim();
        if (!trimmedEmail) return;

        if (!isValidEmail(trimmedEmail)) {
            toast.error('Please enter a valid email address');
            return;
        }

        if (!notificationEmails.includes(trimmedEmail)) {
            setNotificationEmails([...notificationEmails, trimmedEmail]);
            setEmailInput('');
        } else {
            toast.error('This email is already added');
        }
    };

    const removeEmail = (emailToRemove: string) => {
        setNotificationEmails(notificationEmails.filter(email => email !== emailToRemove));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!machine) return;
        if (newPinCode.length !== 6) {
            toast.dismiss();
            toast.error('Pin Code must be exactly 6 characters long.');
            return;
        }

        if (!imageFile && !imageUrl) {
            toast.dismiss();
            toast.error('Please provide a valid image.');
            return;
        }

        setUploading(true);
        try {
            let splashScreenUrl = imageUrl || '';

            if (imageFile) {
                const presignedUrl = await getPresignedUrl();
                await uploadImage(presignedUrl);
                splashScreenUrl = presignedUrl.split('?')[0];
            }

            await machineClient.updateVendingMachine({
                machineUuid: machine.uuid,
                pinCode: newPinCode,
                splashScreenUrl: splashScreenUrl,
                contactNumber: contactNumber,
                notificationEmails: notificationEmails,
                name: machineName,
            });
            toast.dismiss();
            toast.success('Machine updated successfully');
            navigate('/lockers');
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to update vending machine or upload image: ' + (error as Error).message);
            console.error('Error:', error);
        } finally {
            setUploading(false);
        }
    };

    if (isLoading) {
        return <div className="p-4 text-center">Loading machine...</div>;
    }

    if (!machine) {
        return (
            <div className="p-4 text-center">
                <p className="text-gray-600">Machine not found.</p>
                <button onClick={() => navigate('/lockers')} className="mt-4 text-indigo-600 hover:text-indigo-800">Back to Lockers</button>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-lg font-bold">Edit Machine: {machine?.name}</h2>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4 relative">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Machine Name</label>
                    <input
                        type="text"
                        value={machineName}
                        onChange={(e) => setMachineName(e.target.value)}
                        placeholder="Machine Name"
                        className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring focus:ring-blue-500 w-full"
                        aria-label="Machine Name"
                    />
                </div>
                <label className="block text-sm font-medium text-gray-700">Pin Code</label>
                <input
                    type="text"
                    value={newPinCode}
                    onChange={(e) => setNewPinCode(e.target.value)}
                    placeholder="New Pin Code"
                    className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                    aria-label="New Pin Code"
                />
                <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                    <input
                        type="tel"
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        placeholder="Contact Number"
                        className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring focus:ring-blue-500 w-full"
                        aria-label="Contact Number"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Notification Emails</label>
                    <p className="text-sm text-gray-500 mt-1 mb-2">
                        These email addresses will receive notifications for events on this machine:
                    </p>
                    <ul className="text-sm text-gray-500 list-disc list-inside mb-3 space-y-0.5">
                        <li>Sale item sold (refill needed)</li>
                        <li>Lease expired</li>
                        <li>Locker door left open</li>
                        <li>Item pending inspection</li>
                    </ul>
                    <p className="text-xs text-gray-400 mb-2">
                        Note: Machine offline notifications are configured separately under Organization Settings.
                    </p>
                    <div className="border border-gray-300 p-3 rounded-md focus-within:ring focus-within:ring-blue-500">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {notificationEmails.map((email, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full"
                                >
                                    {email}
                                    <button
                                        type="button"
                                        onClick={() => removeEmail(email)}
                                        className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                                        aria-label={`Remove ${email}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                        <input
                            type="text"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={handleEmailKeyDown}
                            placeholder="Type email and press Enter or comma"
                            className="w-full border-none focus:outline-none"
                            aria-label="Add Notification Email"
                        />
                    </div>
                </div>
                <label className="block text-sm font-medium text-gray-700">Screen Image</label>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                    aria-label="Upload Image"
                />
                <img src={imageUrl ? imageUrl : machine.splashScreenUrl} alt="Preview" className="mt-2 w-32 h-32 object-cover" />
                {uploading && (
                    <div className="absolute inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center">
                        <div className="text-white">Uploading...</div>
                    </div>
                )}
                <div className="flex mt-4">
                    <button type="button" onClick={() => navigate('/lockers')} className="bg-gray-300 text-black p-2 mr-2 rounded-md">Cancel</button>
                    <button type="submit" className="bg-blue-600 text-white p-2 rounded-md">Update</button>
                </div>
            </form>

            {machine && (
                <div className="mt-6">
                    <button
                        type="button"
                        onClick={() => navigate(`/payment-settings/${machineUuid}`, { state: { machine } })}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                        <CreditCard className="w-4 h-4" />
                        Payment Settings
                    </button>
                </div>
            )}
        </div>
    );
};

export default EditMachine;
